import {match, Pattern} from 'ts-pattern';

import {always, isNil, isNotNil} from 'ramda';

import type {GetTenantV2ApiRes} from '@dms/api/base';
import type {GetCustomerV2ApiResponse} from '@dms/api/customer';
import {getNaturalPersonFullName} from '@dms/shared';

import type {Nullish} from 'shared/web';

import type {DefaultSignatoriesFormData} from '../types/DefaultSignatoriesFormData';
import {getTrimmedFormValue} from './getTrimmedFormValue';

const NATURAL_PERSON_LEGAL_FORM = 'NATURAL_PERSON';

/**
 * Pre-fills the customer signatory. A given contact person of the customer signs on the customer's
 * behalf (e.g. for a company consent), otherwise the founding person or the first contact signs.
 * A given contact person who is no longer one of the customer's contacts is left for the operator
 * to fill in, so that nobody else's contact details are pre-filled.
 */
export const getCustomerSignatoryDefaults = (
  customer: GetCustomerV2ApiResponse | Nullish,
  tenant: Pick<GetTenantV2ApiRes, 'publicContacts'> | Nullish,
  contactPersonId?: string | Nullish
): DefaultSignatoriesFormData[string] | null => {
  const contactPerson = customer?.contacts?.find((contact) => contact.id === contactPersonId);
  const customerInfo = contactPerson ?? customer?.foundingPerson ?? customer?.contacts?.[0];
  const isContactPersonMissing = isNotNil(contactPersonId) && isNil(contactPerson);

  return match({customer, customerInfo, isContactPersonMissing})
    .with({customer: Pattern.nonNullable, isContactPersonMissing: true}, ({customer}) => ({
      id: customer.id,
      name: null,
      email: null,
      phoneNumber: {countryCode: '', number: '', prefix: ''},
    }))
    .with(
      {customer: Pattern.nonNullable, customerInfo: Pattern.nonNullable},
      ({customer, customerInfo}) => {
        const email = customerInfo.emails?.[0]?.email;
        const phoneNumber = customerInfo.phoneNumbers?.[0] ?? tenant?.publicContacts?.phoneNumber;
        const isSignedByPerson =
          isNotNil(contactPerson) ||
          customer.contractInformation?.[0]?.legalForm === NATURAL_PERSON_LEGAL_FORM;
        const name = isSignedByPerson
          ? getNaturalPersonFullName(customerInfo)
          : customer.businessInfo?.businessInfo?.tradeName;

        return {
          id: customer.id,
          name: name ?? '',
          email: email ?? null,
          phoneNumber: {
            countryCode: getTrimmedFormValue(phoneNumber?.countryCode),
            number: getTrimmedFormValue(phoneNumber?.number),
            prefix: getTrimmedFormValue(phoneNumber?.prefix),
          },
        };
      }
    )
    .otherwise(always(null));
};
